import { Button, Grid, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme, ThemeProvider, Typography, createTheme, useMediaQuery } from "@mui/material";
import React, { useState, useEffect, Fragment } from "react";
import { PageHeader } from "../../components/PageHeader";
import { useStyles } from "../../common/appStyles";
import { RootState } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { PageEnum, RoleEnum } from "../../types/enums";
import { showSnackbar } from "../../services/snackbar.service";
import { SubmissionDetail } from "./SubmissionDetail";
import { IBasicUserDashboardInfo, ISubmissionState, IUserCompetencySubmissionInformation, IYouthDashboardInfo } from "../../types/youths";
import { GetCertificate, GetYouthDashboardInfo } from "../../services/user-service";
import { useLocation, useNavigate } from "react-router-dom";
import { updateSubmissionDialog } from "../../store/submissionDialogState";
import { BannerAd } from "../../components/main-layout/BannerAd";

/**
 * @remarks this page a user's name school an region as well as
 * a table which contains their competency/submission details as {@link IYouthDashboardInfo}. 
 * It shows the current status of each competency, as well as a button to take action on that competency.
 * The actions will be Create, Update, or View a submission when clicked it will
 * take the user to the {@link SubmissionDetail} page.
 * @returns an Html component with a Youth's name, school, and region
 *  as well as a table of a Youth's competency information.
*/
export const YouthDashboard = () => {
    const id = useLocation().state;
    const classes = useStyles();
    const navigate = useNavigate();
    const userRole = useSelector((state: RootState) => state.roles);
    const currentPage = userRole.userPageAccess[PageEnum.YouthDashboard];
    const [isCertAwarded, setIsCertAwarded] = useState<boolean>(false);
    const user = useSelector((state: RootState) => state.userDetail);
    const submissionisOpenState = useSelector((state: RootState) => state.submissionDialog);
    const dispatch = useDispatch();
    const [rows, setRows] = useState<IUserCompetencySubmissionInformation[]>([]);
    const [selectedUser, setselectedUser] = React.useState<IBasicUserDashboardInfo>();
    const [selectedSubmission, setSelectedSubmission] = useState<IUserCompetencySubmissionInformation>();
    const [showSubmissionDetail, setShowSubmissionDetail] = useState(false);
    const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
    const [pageTitle, setPageTitle] = useState("Youth Dashboard");
    const [titleText, setTitleText] = useState("");
    const theme = createTheme({
        palette: {
            primary: {
                main: '#484A57'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#484A57'
            },
            body1: {
                color: 'black'
            },
            subtitle1: {
                color: 'black',
                textDecoration: 'none',
                '&:visited': {
                    color: '#06bd9b',
                },

            }
        },
    });

    const handleClose = () => {
        const current: ISubmissionState = { isOpen: true };
        dispatch(updateSubmissionDialog(current));
        setShowSubmissionDetail(false)
        let userId: number = 0;
        if (id !== null) {
            userId = parseInt(id.id, 10);
        }
        if (user.roleId === RoleEnum.Youth || userId !== 0) {
            getYouthInfo(userId !== 0 ? userId : undefined);
        }
    }

    const handleAction = (event, row: IUserCompetencySubmissionInformation) => {
        switch (row.submissionStage) {
            case 'Not Started':
                createSubmission(row);
                break;
            case 'Draft In-Progress':
                editSubmissionDraft(row);
                break;
            case 'Awaiting Judge Assignment':
            case 'Judge Assigned—Awaiting Review':
            case 'Submission Denied by Judge 1—Awaiting Second Judge Assignment':
            case "Submission Denied by Judge 1—Awaiting Second Judge's Review":
            case 'Submission Denied by Both Judges':
            case 'Submission Approved':
                viewSubmission(row);
                break;
            default:
                console.error("error fetching submission status");
        }
    }

    const formatDate = (input) => {
        const date = input instanceof Date ? input : new Date(input);
        if (isNaN(date.getTime())) {
            console.error('Invalid input for formatDate:', input);
            return '';
        }

        const formattedDate = new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit'
        }).format(date);
        return formattedDate;
    }

    const createSubmission = (row: IUserCompetencySubmissionInformation) => {
        if (user.roleId === RoleEnum.Youth) {
            setTitleText("Create Submission");
            setSelectedSubmission(row)
            setShowSubmissionDetail(true);
        }
    }

    const editSubmissionDraft = (row: IUserCompetencySubmissionInformation) => {
        if (user.roleId === RoleEnum.Youth) {
            setTitleText("Edit Submission");
            setSelectedSubmission(row);
            setShowSubmissionDetail(true);
        }
        else {
            setTitleText("Submission Details");
            setSelectedSubmission(row)
            setShowSubmissionDetail(true);
        }
    }

    const viewSubmission = (row: IUserCompetencySubmissionInformation) => {
        if (user.roleId === RoleEnum.Youth) {
            setTitleText("Submission Details");
            setSelectedSubmission(row)
            setShowSubmissionDetail(true);
        }
        else {
            setTitleText("Submission Details");
            setSelectedSubmission(row)
            setShowSubmissionDetail(true);
        }
    }

    const getStageDate = (row: IUserCompetencySubmissionInformation) => {
        switch (row.submissionStage) {
            case 'Not Started':
                return "_";
            case 'Draft In-Progress':
                return formatDate(row.submissionCreateDateTime);
            case 'Submission Denied by Both Judges':
            case 'Submission Approved':
                return formatDate(row.submissionDecisionDateTime);
            default:
                return formatDate(row.submissionSubmitDateTime);
        }
    }

    const setButtonText = (row: IUserCompetencySubmissionInformation) => {
        switch (row.submissionStage) {
            case 'Not Started':
                return "Create";
            case 'Draft In-Progress':
                if (currentPage?.create) {
                    return "Update";
                }
                else {
                    return "View";
                }
            case 'Awaiting Judge Assignment':
            case 'Judge Assigned—Awaiting Review':
            case 'Submission Denied by Judge 1—Awaiting Second Judge Assignment':
            case "Submission Denied by Judge 1—Awaiting Second Judge's Review":
            case 'Submission Denied by Both Judges':
            case 'Submission Approved':
                return "View";
            default:
                console.error("Error getting button Text");
        }
    }

    const getCert = async () => {
        if (isCertAwarded) {
            const certBlob = await GetCertificate();
            const blob = new Blob([certBlob], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = user.lastName + " " + user.firstName + " YEC Certification";
            anchor.click();
            URL.revokeObjectURL(url);
        }
    }

    const getYouthInfo = (id?: number) => {
        GetYouthDashboardInfo(id)
            .then(response => {
                setRows(response.userCompetencySubmissionInformation)
                setselectedUser({
                    userId: response.basicUserDashboardInfo.userId,
                    firstName: response.basicUserDashboardInfo.firstName,
                    lastName: response.basicUserDashboardInfo.lastName,
                    schoolId: response.basicUserDashboardInfo.schoolId,
                    school: response.basicUserDashboardInfo.school,
                    regionId: response.basicUserDashboardInfo.regionId,
                    region: response.basicUserDashboardInfo.region
                });
            })
            .catch((error) => {
                showSnackbar('Error geting data', 'error', 5000);
            });
    }

    useEffect(() => {
        let userId: number = 0;
        if (id) {
            userId = parseInt(id.id, 10);
        }
        if (user.roleId === RoleEnum.Youth) {
            getYouthInfo();
        }
        else if (userId !== 0) {
            getYouthInfo(userId);
        }
    }, [user]);

    const onNavSchool = () => {
        navigate(`/schoolcoordinatordashboard`, { state: { schoolId: selectedUser?.schoolId } });
    }

    const onNavRegion = () => {
        const regionList: number[] = [];
        const regionId = selectedUser?.regionId;
        if (regionId !== null && regionId !== undefined) {
            regionList.push(regionId);
        }
        navigate('/casedetail',
            {
                state: {
                    caseType: [],
                    region: regionList,
                    competency: [],
                    searchQuery: "",
                    assigned: false
                }
            }
        )
    }

    useEffect(() => {
        setIsCertAwarded(rows.every(row => row?.isAwarded === true));
    }, [rows]);

    return (
        <Fragment>
            <ThemeProvider theme={theme}>
                <Grid
                    container
                    className={classes.formContainer}
                    spacing={1}
                    justifyContent="center"
                    alignItems={'center'}
                >
                    {user.roleId >= RoleEnum.CredentialingManager && !lgUp && <Grid className={classes.bannerTopPadding}>
                        <BannerAd />
                    </Grid>}
                    <Grid item sm={12} className='justify-center' padding="5px">
                        <Grid
                            container
                            spacing={1}
                            direction={{ xs: 'column', md: 'row' }}
                            justifyContent="center"
                            alignItems="center"
                            className={lgUp ? classes.fixGridMargin : classes.bannerTopPadding}
                        >
                            <Grid item xs={3}><PageHeader title={pageTitle} onBackClick={undefined} /></Grid>
                            <Grid item xs={12}>
                                {selectedUser ?
                                    <Grid
                                        container
                                        justifyContent={"center"}
                                        alignItems={"flex-start"}
                                        spacing={1}
                                        direction={"column"}
                                        sx={{ paddingLeft: "5vh", paddingBottom: "1vh" }}
                                    >
                                        <Grid item xs={4} md={2}>
                                            <Typography variant={"subtitle1"}>
                                                Region: {user.roleId >= RoleEnum.SchoolCoordinator ? selectedUser?.region : <a onClick={onNavRegion} style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer', }}>{selectedUser?.region}</a>}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4} md={0}>
                                            <Typography variant="subtitle1">
                                                School: {user.roleId === RoleEnum.Youth ? selectedUser?.school : <a onClick={onNavSchool} style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer', }}>{selectedUser?.school}</a>}
                                            </Typography>
                                        </Grid>
                                        {user?.roleId !== RoleEnum.Youth &&
                                            <Grid item xs={4} >
                                                <Typography variant={"body1"}>
                                                    {selectedUser?.firstName} {selectedUser?.lastName}
                                                </Typography>
                                            </Grid>
                                        }
                                    </Grid>
                                    :
                                    <Grid
                                        container
                                        justifyContent={"center"}
                                        alignItems={"flex-start"}
                                        spacing={1}
                                        direction={"column"}
                                        sx={{ paddingLeft: "5vh", paddingBottom: "1vh" }}
                                    >
                                        <Grid item xs={4} md={2}>
                                            <Skeleton variant="text" width={'20vh'} />
                                        </Grid>
                                        <Grid item xs={4} md={0}>
                                            <Skeleton variant="text" width={'20vh'} />
                                        </Grid>
                                        {user?.roleId !== RoleEnum.Youth && <Grid item xs={4} >
                                            <Skeleton variant="text" width={'20vh'} />
                                        </Grid>}
                                    </Grid>
                                }
                            </Grid>
                            <Grid
                                container
                                justifyContent={'center'}
                                alignItems={"center"}
                                spacing={1}
                            >
                                <Grid item md={11} className={classes.tableGrid} xs={12} maxWidth={{ xs: '45vh', md: '100%' }}>
                                    {(Array.isArray(rows) && rows.length !== 0) ? <Paper elevation={3}>
                                        <TableContainer>
                                            <Table stickyHeader size="small" aria-label="school table" className={classes.tableSpacing}>
                                                <TableHead>
                                                    <TableRow className={classes.tableHead}>
                                                        <TableCell key="name">Competency</TableCell>
                                                        <TableCell key="SchoolName">Submission Stage</TableCell>
                                                        <TableCell key="RegionName">Submission Stage Date</TableCell>
                                                        <TableCell key="RoleName">Submission Title</TableCell>
                                                        <TableCell key="actions" align="center">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {rows.map((row: IUserCompetencySubmissionInformation) => (
                                                        <TableRow
                                                            key={row.competencyId}
                                                            className={classes.table}
                                                        >
                                                            <TableCell component="th" scope="row">{row.competency}</TableCell>
                                                            <TableCell align="left">{row.submissionStage}</TableCell>
                                                            <TableCell align="left">{getStageDate(row)}</TableCell>
                                                            <TableCell align="left">{row.title}</TableCell>
                                                            {(row.submissionStage === 'Not Started' && user.roleId !== RoleEnum.Youth) ||
                                                                <TableCell key="actions" align="center">
                                                                    <Button
                                                                        onClick={e => handleAction(e, row)}
                                                                        className="app-button ml10"
                                                                        color='primary'
                                                                        size="small"
                                                                        variant={row.submissionStage === 'Not Started' ? 'contained' : 'outlined'}
                                                                        fullWidth
                                                                    >
                                                                        {setButtonText(row)}
                                                                    </Button>
                                                                </TableCell>}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                        :
                                        <Grid container direction={"column"} rowSpacing={1}>
                                            <Grid item xs={12}>
                                                <Skeleton variant='rectangular' width={"100%"} height={"2vh"} />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Skeleton variant='rounded' width={'100%'} height={'30vh'} />
                                            </Grid>
                                        </Grid>
                                    }
                                </Grid>
                                {(isCertAwarded && user.roleId === RoleEnum.Youth && rows.length !== 0) && < Grid item xs={12} >
                                    <Grid
                                        container
                                        direction={"column"}
                                        justifyContent={"center"}
                                        alignItems={"center"}
                                        rowSpacing={"2vh"}
                                        sx={{ paddingTop: "5vh" }}
                                    >
                                        <Grid item>
                                            <Typography variant="h6">
                                                Congratulations, you've been awarded badges for every competency!
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Button onClick={getCert} variant={"contained"}>
                                                Get Certificate
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>}
                            </Grid>
                            {currentPage?.read && <Grid item sm={12}>
                                {showSubmissionDetail && <SubmissionDetail
                                    showDialog={showSubmissionDetail}
                                    selectedSubmission={selectedSubmission}
                                    selectedUser={selectedUser}
                                    titleText={titleText}
                                    handleClose={handleClose}
                                />}
                            </Grid>}
                        </Grid>
                    </Grid>
                </Grid>
            </ThemeProvider>
        </Fragment >
    );
}

